import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, Loader2, Link as LinkIcon, ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { BACKEND_URL } from '../lib/api';

/**
 * ImageUploader — small reusable image picker that talks to /api/upload/image
 * (the same backend endpoint used for event images, so storage + auth +
 * validation are identical).
 *
 * Props:
 *   value           — current image URL (controlled)
 *   onChange(url)   — fires with the uploaded URL (or '' on remove)
 *   aspect          — 'square' (logo) | 'wide' (cover). Drives preview ratio + helper text.
 *   helper          — optional override for the helper line under the field
 *   label           — label rendered above the picker
 *   testId          — base data-testid; subparts get suffixes
 */
const ImageUploader = ({
  value,
  onChange,
  aspect = 'square',
  helper,
  label,
  testId = 'image-uploader',
}) => {
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');
  const fileInput = useRef(null);

  const aspectClass = aspect === 'wide' ? 'aspect-[16/6]' : 'aspect-square';
  const defaultHelper =
    aspect === 'wide'
      ? 'Recommended: wide landscape image · JPG, PNG or WebP · max 5MB'
      : 'Recommended: square image · JPG, PNG or WebP · max 5MB';

  const ALLOWED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const MAX_BYTES = 5 * 1024 * 1024;

  const upload = async (file) => {
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      toast.error('Please upload a JPG, PNG or WebP image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Image must be under 5MB.');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(`${BACKEND_URL}/api/upload/image`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange?.(r.data?.url || '');
      toast.success('Image uploaded.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    // Reset so re-selecting the same file still fires onChange
    if (fileInput.current) fileInput.current.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const remove = () => {
    onChange?.('');
    toast.success('Image removed.');
  };

  const applyUrl = () => {
    const v = (urlDraft || '').trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v) && !v.startsWith('/api/files/')) {
      toast.error('Image URL must start with http(s):// or be an existing /api/files/ path.');
      return;
    }
    onChange?.(v);
    setShowUrlInput(false);
    setUrlDraft('');
    toast.success('Image URL set.');
  };

  // Resolve preview src — relative /api/files/... paths need the backend host.
  const previewSrc = (() => {
    if (!value) return null;
    if (value.startsWith('/api/')) return `${BACKEND_URL}${value}`;
    return value;
  })();

  return (
    <div data-testid={testId}>
      {label && <p className="text-sm font-medium text-[#1F3D2B] mb-1.5">{label}</p>}

      {previewSrc ? (
        <div className="relative">
          <div className={`w-full ${aspectClass} rounded-xl overflow-hidden bg-[#F4F6F3] border border-[#E5E9E6]`}>
            <img
              src={previewSrc}
              alt={label || 'Preview'}
              className="w-full h-full object-cover"
              data-testid={`${testId}-preview`}
              onError={(e) => { e.currentTarget.style.opacity = '0.4'; }}
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="rounded-full text-xs h-9 px-3"
              data-testid={`${testId}-replace`}
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
              Replace
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={remove}
              disabled={uploading}
              className="rounded-full text-xs h-9 px-3 text-rose-700 hover:bg-rose-50"
              data-testid={`${testId}-remove`}
            >
              <X className="w-3.5 h-3.5 mr-1.5" /> Remove
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          disabled={uploading}
          className={`w-full ${aspectClass} rounded-xl border-2 border-dashed border-[#E5E9E6] bg-[#FAFAF7] hover:border-[#C9A27A]/60 hover:bg-[#F4F6F3] transition-colors flex flex-col items-center justify-center text-[#4A5D50] text-xs gap-1.5 cursor-pointer`}
          data-testid={`${testId}-dropzone`}
        >
          {uploading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Uploading…</span>
            </>
          ) : (
            <>
              <ImageIcon className="w-5 h-5 text-[#C9A27A]" />
              <span className="font-medium text-[#1F3D2B]">Click or drag to upload</span>
              <span className="text-[10px]">{helper || defaultHelper}</span>
            </>
          )}
        </button>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={onPick}
        className="sr-only"
        data-testid={`${testId}-file-input`}
      />

      {/* Optional: paste an external URL instead of uploading. */}
      <div className="mt-2">
        {showUrlInput ? (
          <div className="flex gap-2">
            <Input
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              placeholder="https://…"
              className="h-9 text-xs"
              data-testid={`${testId}-url-input`}
            />
            <Button type="button" variant="outline" onClick={applyUrl} className="h-9 px-3 text-xs rounded-md">
              Use
            </Button>
            <Button type="button" variant="ghost" onClick={() => setShowUrlInput(false)} className="h-9 px-2 text-xs">
              Cancel
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowUrlInput(true)}
            className="text-[11px] text-[#4A5D50] hover:text-[#1F3D2B] inline-flex items-center gap-1 underline decoration-dotted"
            data-testid={`${testId}-url-toggle`}
          >
            <LinkIcon className="w-3 h-3" /> Or paste an image URL
          </button>
        )}
      </div>
    </div>
  );
};

export default ImageUploader;
